type DiffOp =
  | {
      kind: "equal";
      value: string;
    }
  | {
      kind: "delete";
      value: string;
    }
  | {
      kind: "insert";
      value: string;
    };

type BuildUnifiedDiffInput = {
  oldText: string;
  newText: string;
  oldLabel: string;
  newLabel: string;
  maxChars?: number;
};

const DEFAULT_MAX_DIFF_CHARS = 16_000;
const MAX_LCS_CELLS = 2_000_000;

function computeOps(oldLines: string[], newLines: string[]): DiffOp[] {
  const oldLength = oldLines.length;
  const newLength = newLines.length;
  const table: number[][] = Array.from({ length: oldLength + 1 }, () =>
    Array<number>(newLength + 1).fill(0),
  );

  for (let i = oldLength - 1; i >= 0; i -= 1) {
    for (let j = newLength - 1; j >= 0; j -= 1) {
      if (oldLines[i] === newLines[j]) {
        table[i][j] = table[i + 1][j + 1] + 1;
      } else {
        table[i][j] = Math.max(table[i + 1][j], table[i][j + 1]);
      }
    }
  }

  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;

  while (i < oldLength && j < newLength) {
    if (oldLines[i] === newLines[j]) {
      ops.push({ kind: "equal", value: oldLines[i] ?? "" });
      i += 1;
      j += 1;
      continue;
    }

    if (table[i + 1][j] >= table[i][j + 1]) {
      ops.push({ kind: "delete", value: oldLines[i] ?? "" });
      i += 1;
      continue;
    }

    ops.push({ kind: "insert", value: newLines[j] ?? "" });
    j += 1;
  }

  while (i < oldLength) {
    ops.push({ kind: "delete", value: oldLines[i] ?? "" });
    i += 1;
  }

  while (j < newLength) {
    ops.push({ kind: "insert", value: newLines[j] ?? "" });
    j += 1;
  }

  return ops;
}

export function buildUnifiedDiff(input: BuildUnifiedDiffInput): string {
  const maxChars = input.maxChars ?? DEFAULT_MAX_DIFF_CHARS;
  if (input.oldText === input.newText) {
    return "";
  }

  const oldLines = input.oldText.split(/\r?\n/);
  const newLines = input.newText.split(/\r?\n/);
  const cells = (oldLines.length + 1) * (newLines.length + 1);

  if (cells > MAX_LCS_CELLS) {
    return [
      `--- ${input.oldLabel}`,
      `+++ ${input.newLabel}`,
      "@@ diff omitted @@",
      "Diff is too large to render safely.",
    ].join("\n");
  }

  const ops = computeOps(oldLines, newLines);
  const output: string[] = [
    `--- ${input.oldLabel}`,
    `+++ ${input.newLabel}`,
    `@@ -1,${oldLines.length} +1,${newLines.length} @@`,
  ];

  for (const op of ops) {
    if (op.kind === "equal") {
      output.push(` ${op.value}`);
      continue;
    }
    if (op.kind === "delete") {
      output.push(`-${op.value}`);
      continue;
    }
    output.push(`+${op.value}`);
  }

  const diff = output.join("\n");
  if (diff.length <= maxChars) {
    return diff;
  }

  return `${diff.slice(0, maxChars)}\n... [diff truncated]`;
}
